import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useStreamAuth } from '../hooks/useStreamAuth';
import { useWatchlist } from '../hooks/useWatchlist';
import { useContinueWatching } from '../hooks/useContinueWatching';
import { userDataService } from '../services/UserDataService';
import ChannelCard from '../components/ChannelCard';
import LoadingSpinner from '../components/auth/LoadingSpinner';
import ProgressBar from '../components/common/ProgressBar';
import { useAuthContext } from '../contexts/AuthContext';

const MyListsPage = () => {
  const { loginWithRedirect } = useAuthContext();
  const { user, isAuthenticated } = useStreamAuth();
  const navigate = useNavigate();
  const { tab } = useParams();

  const {
    watchlistData: watchlist,
    loading: watchlistLoading,
    error: watchlistError,
    isRemoving,
    removeFromWatchlist,
  } = useWatchlist();
  const { continueWatchingData: continueWatching, loading: continueWatchingLoading } =
    useContinueWatching();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Define available tabs with their configurations
  const tabs = [
    {
      id: 'watchlist',
      label: 'My List',
      subtitle: 'Saved favorites',
      count: watchlist.length,
      path: '/my-lists/watchlist',
    },
    {
      id: 'continue-watching',
      label: 'Continue Watching',
      subtitle: 'Pick up where you left off',
      count: continueWatching.length,
      path: '/my-lists/continue-watching',
    },
  ];

  // Determine active tab from URL parameter, default to 'watchlist'
  const activeTab = tab && tabs.find(t => t.id === tab) ? tab : 'watchlist';

  // Redirect to default tab if no tab specified
  useEffect(() => {
    if (!tab) {
      navigate('/my-lists/watchlist', { replace: true });
    }
  }, [tab, navigate]);

  useEffect(() => {
    if (isAuthenticated && user) {
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  // Combine loading states
  const isLoadingData =
    continueWatchingLoading || isLoading || (watchlistLoading && watchlist.length === 0);
  const hasError = watchlistError || error;

  const handleRemoveFromWatchlist = async channelId => {
    try {
      await removeFromWatchlist(channelId);
    } catch (error) {
      console.error('MyListsPage: Error removing from watchlist:', error);
      setError("We couldn't update your list right now. Please try again.");
    }
  };

  const handleItemClick = async item => {
    try {
      const slug = item.parent_channel?.slug || item.slug;

      if (item.content_type === 'series' || item.childChannels?.length > 0) {
        const latestWatchedEpisode = userDataService.findLatestWatchedEpisode(item._id);
        if (latestWatchedEpisode) {
          navigate(`/watch/${slug}?_id=${latestWatchedEpisode.episode._id}`);
        } else {
          navigate(`/watch/${slug}`);
        }
      } else {
        navigate(`/watch/${slug}`);
      }
    } catch (error) {
      console.error('MyListsPage: Error handling item click:', error);
      navigate(`/watch/${item.slug}`);
    }
  };

  const handleTabClick = tabId => {
    const selectedTab = tabs.find(t => t.id === tabId);
    if (selectedTab) {
      navigate(selectedTab.path);
    }
  };

  const getTabIcon = tabId => {
    switch (tabId) {
      case 'watchlist':
        return (
          <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5A5.5 5.5 0 017.5 3 5.9 5.9 0 0112 5.09 5.9 5.9 0 0116.5 3 5.5 5.5 0 0122 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        );
      case 'continue-watching':
        return (
          <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
            <path d="M8 5v14l11-7L8 5z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getTabDescription = () => {
    switch (activeTab) {
      case 'watchlist':
        return 'Your favorite fights, films, and shows in one place.';
      case 'continue-watching':
        return 'Jump back into what you started watching.';
      default:
        return 'Manage your saved and personalized content.';
    }
  };

  const renderEmptyState = ({ tabId, title, description, ctaLabel }) => (
    <div className="rounded-2xl border border-dashed border-white/20 bg-black/20 px-6 py-14 text-center">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-accent">
        {getTabIcon(tabId)}
      </div>
      <h3 className="mb-2 text-xl font-semibold text-white">{title}</h3>
      <p className="mx-auto mb-6 max-w-md text-white/70">{description}</p>
      <button
        className="rounded-md bg-accent px-5 py-2.5 font-semibold text-primary transition-colors hover:bg-accent-hover"
        onClick={() => navigate('/')}
      >
        {ctaLabel}
      </button>
    </div>
  );

  const renderWatchlistContent = () => {
    if (watchlist.length === 0) {
      return renderEmptyState({
        tabId: 'watchlist',
        title: 'Your list is empty',
        description:
          'Add content to My List from any card or detail page to build your personal lineup.',
        ctaLabel: 'Browse Content',
      });
    }

    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {watchlist.map((item, index) => (
          <div key={item._id || index} className="relative">
            <ChannelCard
              channel={item}
              style="watchlist"
              index={index}
              externalClickHandle={() => handleItemClick(item)}
            />
            <button
              onClick={() => handleRemoveFromWatchlist(item._id)}
              disabled={isRemoving(item._id)}
              className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/75 text-sm text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
              title="Remove from list"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    );
  };

  const renderContinueWatchingContent = () => {
    if (continueWatching.length === 0) {
      return renderEmptyState({
        tabId: 'continue-watching',
        title: 'Nothing to continue',
        description:
          'Start watching an event, movie, or series and your progress will appear here.',
        ctaLabel: 'Start Watching',
      });
    }

    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {continueWatching.map((item, index) => (
          <div key={item._id || index} className="relative">
            <ChannelCard
              channel={item}
              style="continue-watching"
              index={index}
              externalClickHandle={() => handleItemClick(item)}
            />
            {(item.progress || item.resumption_data?.progress) && (
              <div className="absolute bottom-2 left-2 right-2">
                <ProgressBar
                  progress={item.progress || item.resumption_data?.progress}
                  className="h-1 bg-white/20"
                  fillClassName="bg-accent"
                />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'watchlist':
        return renderWatchlistContent();
      case 'continue-watching':
        return renderContinueWatchingContent();
      default:
        return renderWatchlistContent();
    }
  };

  const getPageTitle = () => {
    const currentTab = tabs.find(t => t.id === activeTab);
    return currentTab ? currentTab.label : 'My Lists';
  };

  const currentTab = tabs.find(t => t.id === activeTab);

  // Authentication check
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-primary text-white">
        <div className="container mx-auto px-4 py-16">
          <div className="mx-auto max-w-xl rounded-2xl border border-white/10 bg-black/25 p-10 text-center">
            <h2 className="mb-4 text-2xl font-bold">Sign in required</h2>
            <p className="mb-6 text-white/70">You need to sign in to view your lists.</p>
            <button
              onClick={() => loginWithRedirect()}
              className="rounded-md bg-accent px-6 py-2.5 font-semibold text-primary transition-colors hover:bg-accent-hover"
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoadingData) {
    return (
      <div className="min-h-screen bg-primary text-white">
        <div className="container mx-auto px-4 py-8 md:py-10">
          <div className="mb-6 rounded-2xl border border-white/10 bg-gradient-to-r from-black/40 to-black/10 px-6 py-6">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-accent">Library</p>
            <h1 className="mt-2 text-3xl font-display uppercase tracking-wide text-white md:text-4xl">
              My List
            </h1>
            <p className="mt-2 text-white/70">Manage your saved and personalized content.</p>
          </div>

          <div className="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
            <aside className="rounded-2xl border border-white/10 bg-black/25 p-4">
              <div className="space-y-3">
                {[1, 2].map(index => (
                  <div key={index} className="h-14 animate-pulse rounded-lg bg-white/10" />
                ))}
              </div>
            </aside>

            <section className="flex min-h-[320px] items-center justify-center rounded-2xl border border-white/10 bg-black/20 p-6">
              <LoadingSpinner />
            </section>
          </div>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="min-h-screen bg-primary text-white">
        <div className="container mx-auto px-4 py-8 md:py-10">
          <div className="mb-6 rounded-2xl border border-white/10 bg-gradient-to-r from-black/40 to-black/10 px-6 py-6">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-accent">Library</p>
            <h1 className="mt-2 text-3xl font-display uppercase tracking-wide text-white md:text-4xl">
              My List
            </h1>
          </div>

          <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-10 text-center">
            <h3 className="mb-2 text-xl font-semibold text-white">Something went wrong</h3>
            <p className="mb-6 text-white/75">{hasError}</p>
            <button
              className="rounded-md bg-accent px-5 py-2.5 font-semibold text-primary transition-colors hover:bg-accent-hover"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary text-white">
      <div className="container mx-auto px-4 py-8 md:py-10">
        <div className="mb-6 rounded-2xl border border-white/10 bg-gradient-to-r from-black/40 to-black/10 px-6 py-6">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-accent">Library</p>
          <h1 className="mt-2 text-3xl font-display uppercase tracking-wide text-white md:text-4xl">
            My List
          </h1>
          <p className="mt-2 max-w-2xl text-white/70">
            Save the fights and shows you care about and continue your streams.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
          <aside className="h-fit rounded-2xl border border-white/10 bg-black/25 p-3 md:p-4">
            <nav className="space-y-2">
              {tabs.map(tabConfig => (
                <button
                  key={tabConfig.id}
                  onClick={() => handleTabClick(tabConfig.id)}
                  className={`w-full rounded-lg border px-3 py-3 text-left transition-all ${
                    activeTab === tabConfig.id
                      ? 'border-accent/60 bg-accent/20 text-white'
                      : 'border-white/10 bg-white/[0.03] text-white/80 hover:border-white/25 hover:bg-white/[0.06]'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <span className="text-accent">{getTabIcon(tabConfig.id)}</span>
                      <div>
                        <p className="font-semibold">{tabConfig.label}</p>
                        <p className="text-xs text-white/60">{tabConfig.subtitle}</p>
                      </div>
                    </div>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                        activeTab === tabConfig.id ? 'bg-black/25' : 'bg-white/10'
                      }`}
                    >
                      {tabConfig.count}
                    </span>
                  </div>
                </button>
              ))}
            </nav>
          </aside>

          <section className="rounded-2xl border border-white/10 bg-black/20 p-4 md:p-6">
            <div className="mb-6 flex flex-wrap items-end justify-between gap-3 border-b border-white/10 pb-4">
              <div>
                <h2 className="text-2xl font-bold text-white md:text-3xl">{getPageTitle()}</h2>
                <p className="mt-1 text-sm text-white/65">{getTabDescription()}</p>
              </div>
              <span className="rounded-full border border-white/15 bg-white/[0.04] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white/80">
                {currentTab?.count || 0} items
              </span>
            </div>

            {renderContent()}
          </section>
        </div>
      </div>
    </div>
  );
};

export default MyListsPage;
